import React, { useState, useEffect, useRef } from 'react';
import { Circle, X, Square } from 'lucide-react';
import { Button } from '@v2/components/ui/button';
import { Input } from '@v2/components/ui/input';
import { useAuthenticated } from '@gettactic/hooks';
import { useBreakpoint } from '@gettactic/helpers/src/hooks/useBreakpoint';
import { cn } from '@gettactic/helpers';
import { createPortal } from 'react-dom';
import { useImpersonateContext } from '@webV2/providers/ImpersonateProvider';

const WidgetInput = React.forwardRef<HTMLInputElement, {
  className?: string;
  isMobile?: boolean;
  value: string;
  disabled: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onClick: (e: React.MouseEvent<HTMLInputElement>) => void;
  placeholder: string;
}>(({ className, isMobile = false, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      {...props}
      className={cn(
        'h-9 flex-1 rounded-xl text-sm focus:ring-2 focus:ring-brand/20',
        className
      )}
    />
  );
});

WidgetInput.displayName = 'WidgetInput';

export type ImpersonateWidgetProps = {
  isVisible: boolean;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement>;
};

export const ImpersonateWidget: React.FC<ImpersonateWidgetProps> = ({
  isVisible,
  onClose,
  triggerRef
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [inputValue, setInputValue] = useState('');

  const { impersonate, isBeingImpersonated, stopImpersonating } =
    useImpersonateContext();

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const widgetRef = useRef<HTMLDivElement>(null);
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const hasFocusedRef = useRef(false);

  const { isBreakpoint } = useBreakpoint();
  const isSmallScreen = !isBreakpoint('md');
  const isMobileRef = useRef(isSmallScreen);

  useEffect(() => {
    isMobileRef.current = isSmallScreen;
  }, [isSmallScreen]);

  useEffect(() => {
    const el = document.createElement('div');
    document.body.appendChild(el);
    setPortalContainer(el);
    return () => {
      document.body.removeChild(el);
    };
  }, []);

  const updatePosition = () => {
    if (isVisible && triggerRef?.current && widgetRef?.current && !isSmallScreen) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const widgetRect = widgetRef.current.getBoundingClientRect();
      
      setPosition({
        top: triggerRect.top + triggerRect.height / 2 - widgetRect.height / 2,
        left: triggerRect.right + 12
      });
    }
  };

  useEffect(() => {
    updatePosition();
  }, [isVisible, triggerRef, isSmallScreen]);

  useEffect(() => {
    if (isVisible && !isSmallScreen) {
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('resize', updatePosition);
      };
    }
    return;
  }, [isVisible, isSmallScreen]);

  useEffect(() => {
    if (isSmallScreen && isVisible) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    return;
  }, [isSmallScreen, isVisible]);

  // Focus input when widget becomes visible
  useEffect(() => {
    if (isVisible && !isBeingImpersonated) {
      const inputRef = isMobileRef.current ? mobileInputRef : desktopInputRef;
      const delay = isMobileRef.current ? 300 : 100;
      
      if (inputRef.current && !hasFocusedRef.current) {
        const timeout1 = setTimeout(() => {
          const currentInputRef = isMobileRef.current ? mobileInputRef : desktopInputRef;
          if (currentInputRef.current && document.activeElement !== currentInputRef.current) {
            currentInputRef.current.focus();
          }
        }, delay);
        hasFocusedRef.current = true;
        return () => {
          clearTimeout(timeout1);
        };
      }
    } else if (!isVisible) {
      // Reset focus flag when widget becomes invisible
      hasFocusedRef.current = false;
    }
    return;
  }, [isVisible, isBeingImpersonated]);

  // Handle click outside to close widget
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        widgetRef.current &&
        !widgetRef.current.contains(event.target as Node) &&
        triggerRef?.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Add event listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  const handleSubmit = () => {
    if (inputValue.trim()) {
      impersonate(inputValue.trim());
      setInputValue('');
      onClose();
    }
  };

  const handleStopImpersonation = () => {
    stopImpersonating();
    onClose();
    localStorage.setItem('impersonateSidebarButtonEnabled', 'false');
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: 'impersonateSidebarButtonEnabled',
        newValue: 'false'
      })
    );
  };

  if (!portalContainer || (!isVisible && !isBeingImpersonated)) return null;

  if (!isVisible) return null;
  
  if (isSmallScreen) {
    const topBar = (
      <div className="fixed top-0 left-0 right-0 z-50 h-16 border-b border-gray-200/50 bg-white/95 shadow-lg backdrop-blur-sm">
        <div className="flex h-full items-center justify-center gap-3 px-6">
          <div className="flex w-full max-w-md items-center gap-3">
            {isBeingImpersonated ? (
              <>
                <div className="flex flex-1 items-center text-sm font-medium">
                  <div className="mr-2 h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-red-500" />
                  Currently impersonating {authenticatedUser?.user?.email}
                </div>
                <Button
                  onClick={handleStopImpersonation}
                  className="bg-red-500 hover:bg-red-600"
                >
                  Stop Impersonation
                </Button>
              </>
            ) : (
              <>
                <WidgetInput 
                  ref={mobileInputRef}
                  className="h-10 flex-1 rounded-xl border-gray-200 text-sm focus:ring-2 focus:ring-brand/20" 
                  isMobile={true} 
                  value={isBeingImpersonated ? (authenticatedUser?.user?.email ?? '') : inputValue}
                  disabled={isBeingImpersonated}
                  onChange={(e) => setInputValue(e.target.value)} 
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }} 
                  onClick={(e) => e.stopPropagation()} 
                  placeholder="An email to impersonate" 
                />
                <Button
                  onClick={handleSubmit}
                  className="h-10 rounded-xl bg-gray-800 hover:bg-gray-900"
                >
                  Impersonate
                </Button>
              </>
            )}
          </div>
          <Button
            onClick={onClose}
            className="h-8 w-8 rounded-lg bg-gray-100 p-0 text-gray-600 hover:bg-gray-200"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
    return createPortal(topBar, portalContainer);
  }

  const desktopWidget = (
    <div
      ref={widgetRef}
      className={cn(
        'fixed z-50 flex flex-row rounded-2xl border border-gray-200/50 bg-white/95 p-4 shadow-2xl backdrop-blur-sm will-change-transform',
        'min-w-[280px]'
      )}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
    >
      <div className="flex w-full flex-col">
        <div className="flex items-center gap-2">
          <WidgetInput 
            ref={desktopInputRef}
            value={isBeingImpersonated ? (authenticatedUser?.user?.email ?? '') : inputValue}
            disabled={isBeingImpersonated}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            onClick={(e) => e.stopPropagation()}
            placeholder="An email to impersonate"
          />
          <div className="relative">
            {isBeingImpersonated && (
              <div className="absolute inset-0 z-0">
                <Square className="h-full w-full animate-ping text-red-500/50" />
              </div>
            )}
            <Button
              onClick={(e) => {
                e.stopPropagation();
                if (isBeingImpersonated) {
                  handleStopImpersonation();
                } else {
                  handleSubmit();
                }
              }}
              className={cn(
                'relative z-10 h-9 bg-gray-800 hover:bg-gray-900',
                isBeingImpersonated && 'bg-red-500 hover:bg-red-700'
              )}
            >
              <Circle className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(desktopWidget, portalContainer);
};
